import {useMemo} from 'react';

import {
  hasPermission,
  NAVIGATION_POLICY_REQUEST,
  usePolicyCheck,
} from '#queries/policies/index.js';
import {useCurrentOrgReactPages} from '#queries/react_pages/index.js';
import {useCurrentOrg, useSessionStore} from '#stores/session/index.js';

import {navigationConfig} from '../config/navigation.js';

export function useNavigationItems() {
  const {flags} = useSessionStore();
  const currentOrg = useCurrentOrg();

  const {data: policyData} = usePolicyCheck(NAVIGATION_POLICY_REQUEST);
  const {data: reactPages} = useCurrentOrgReactPages();

  // TEMPORARY: Remove once React SPA migration is complete.
  // Pages in the ReactPages registry that are NOT enabled for this org
  // must use hard links (full page reload) so Rails serves the legacy ERB view.
  const hardLinkGates = useMemo<Record<string, boolean>>(() => {
    if (!reactPages) return {};

    const gates: Record<string, boolean> = {};
    for (const page of reactPages) {
      gates[page.path] = !page.enabled;
    }
    return gates;
  }, [reactPages]);

  const filteredSections = useMemo(() => {
    if (!policyData) return [];

    return navigationConfig.sections
      .map(section => {
        const filteredItems = section.items
          .filter(item => {
            if (item.featureFlag && !flags[item.featureFlag]) {
              return false;
            }

            if (
              item.disabledByOrganizationSetting &&
              currentOrg?.[item.disabledByOrganizationSetting]
            ) {
              return false;
            }

            if (item.policy) {
              if (
                !hasPermission(
                  policyData,
                  item.policy.entity,
                  item.policy.action
                )
              ) {
                return false;
              }
            }

            return true;
          })
          .map(item => ({
            ...item,
            hardLink: item.path in hardLinkGates && hardLinkGates[item.path],
          }));

        return {
          ...section,
          items: filteredItems,
        };
      })
      .filter(section => section.items.length > 0);
  }, [policyData, flags, hardLinkGates, currentOrg]);

  return filteredSections;
}
